import { useMemo, useState } from "react";
import { TargetType, type Device, type User } from "@mcptt/client";
import { useDevices, useMCPTT, useSubscribers, useTick } from "../mcptt/hooks";

interface Props {
  networkId:    string | null;
  selectedId:   string | null;
  onClose:      () => void;
  onLocate:     (deviceId: string) => void;
}

/**
 * Right-side flyout that slides over the map panel when a subscriber
 * is selected. Renders identity, status, GPS, battery, and the action
 * surface for operations the wire actually supports today.
 *
 * Action availability:
 *   - LOCATE ON MAP : lookup-only; uses already-known GPS. Always works.
 *   - LIVE PING     : sends GPS_REQUEST for an immediate single ping.
 *   - SEND TEXT     : sends a one-shot STORAGE_JOB_REGISTER (TargetType=User).
 *   - DIRECT CALL   : disabled — needs Phase 3 audio. Tooltip explains.
 *   - CALL ALERT    : disabled — FleetConsole protocol does not include
 *                     dispatcher→radio call alerts; CA is a permission
 *                     granted TO radios so they can alert dispatchers.
 */
export function SubscriberDetail({ networkId, selectedId, onClose, onLocate }: Props) {
  const { client } = useMCPTT();
  const users   = useSubscribers(networkId);
  const devices = useDevices(networkId);
  const now     = useTick(1000);

  const user: User | undefined = useMemo(
    () => (selectedId ? users.find((u) => u.ID === selectedId) : undefined),
    [users, selectedId],
  );

  // A user can have multiple devices logged in concurrently. Pick the
  // freshest one (highest Time / present GPS / present Parameters).
  const device: Device | undefined = useMemo(() => {
    if (!user) return undefined;
    const own = devices.filter((d) => d.UserID === user.ID);
    if (own.length === 0) return undefined;
    own.sort((a, b) => (b.Time ?? 0) - (a.Time ?? 0));
    return own[0];
  }, [devices, user]);

  const [busyAction, setBusyAction] = useState<"locate" | "text" | null>(null);
  const [textDraft,  setTextDraft]  = useState("");
  const [showText,   setShowText]   = useState(false);
  const [actionMsg,  setActionMsg]  = useState<string | null>(null);

  if (!user) return null;
  // Capture into a non-nullable local so the inner closures stay
  // type-narrowed (TS doesn't narrow free variables across functions).
  const u: User = user;

  const initials = (u.Name || u.Login)
    .split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  const isOnline = !!device;
  const battery  = device?.Parameters?.BATTERY_LEVEL;
  const charging = device?.Parameters?.BATTERY_CHARGING;
  const sg       = device?.Parameters?.SelectedGroupName;
  const lat      = device?.Latitude;
  const lon      = device?.Longitude;
  const gpsTime  = device?.Time;

  function flash(msg: string) {
    setActionMsg(msg);
    window.setTimeout(() => {
      setActionMsg((cur) => (cur === msg ? null : cur));
    }, 3500);
  }

  function handleLocate() {
    if (!device) { flash("No device for this subscriber"); return; }
    if (lat != null && lon != null) onLocate(device.ID);
    else flash("No GPS fix yet — try Live Ping");
  }

  function handleLivePing() {
    if (!networkId || !device) { flash("Cannot ping (no device)"); return; }
    setBusyAction("locate");
    try {
      client.requestGps({
        networkId,
        deviceId: device.ID,
        // periodSec=0 here would be "stop tracking"; 1 is "single ping
        // soon". The manager interprets a small period as a one-shot.
        periodSec: 1,
      });
      flash(`PING SENT \u2192 ${u.Name || u.Login}`);
    } catch (e) {
      flash(`PING FAILED: ${(e as Error).message ?? e}`);
    } finally {
      setBusyAction(null);
    }
  }

  function handleSendText() {
    if (!networkId) return;
    const text = textDraft.trim();
    if (!text) { flash("Empty message"); return; }
    setBusyAction("text");
    try {
      client.sendText({
        networkId,
        targetId:   u.ID,
        targetType: TargetType.User,
        text,
      });
      flash(`MESSAGE SENT \u2192 ${u.Name || u.Login}`);
      setTextDraft("");
      setShowText(false);
    } catch (e) {
      flash(`SEND FAILED: ${(e as Error).message ?? e}`);
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <div className="sub-flyout" role="dialog" aria-label={`Details for ${u.Name || u.Login}`}>
      <div className="sub-flyout-head">
        <div className="sub-flyout-id">
          <div className={`avatar ${isOnline ? "online" : ""}`} style={{ width: 44, height: 44, fontSize: 16 }}>
            {initials}
            <span className="status-dot" />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="sub-flyout-name" title={u.Name || u.Login}>{u.Name || u.Login}</div>
            <div className="sub-flyout-sub">
              {sg ?? (isOnline ? `IMEI ${u.Login}` : "Offline")}
            </div>
          </div>
        </div>
        <button type="button" className="sub-flyout-close" onClick={onClose} aria-label="Close details">
          {"\u2715"}
        </button>
      </div>

      <div className="sub-flyout-body">
        <Row label="Status">
          <span className={`pill ${isOnline ? "ok" : "muted"}`}>{isOnline ? "ONLINE" : "OFFLINE"}</span>
          {device?.VersionName ? <span className="pill muted">{device.VersionName}</span> : null}
        </Row>

        <Row label="Radio Terminal IMEI">
          <span className="t-mono" style={{ color: "var(--bone-dim)" }}>{u.Login}</span>
        </Row>

        <Row label="Battery">
          {battery != null ? (
            <span className={`t-mono ${battery < 20 ? "danger" : "muted"}`}>
              {charging ? "\u26A1 " : ""}{battery}%
            </span>
          ) : <Dim>—</Dim>}
        </Row>

        <Row label="Selected Group">
          {sg ?? <Dim>—</Dim>}
        </Row>

        <Row label="GPS">
          {lat != null && lon != null ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span className="t-mono" style={{ color: "var(--bone-dim)" }}>
                {lat.toFixed(5)}, {lon.toFixed(5)}
              </span>
              {gpsTime ? (
                <span className="t-mono" style={{ color: "var(--steel-600)", fontSize: 11 }}>
                  {formatAge(now - gpsTime)} ago
                  {device?.Accuracy != null ? ` \u00B7 \u00B1${Math.round(device.Accuracy)}m` : ""}
                </span>
              ) : null}
            </div>
          ) : <Dim>No fix</Dim>}
        </Row>

        {/* User ID / Device ID intentionally omitted — backend
            identifiers, not for the dispatcher. The User-ID is still
            available in DOM dev-tools via the `aria-label` on the
            flyout root if anyone needs it for support tickets. */}
      </div>

      {actionMsg ? <div className="sub-flyout-toast">{actionMsg}</div> : null}

      <div className="sub-flyout-actions">
        <button
          type="button"
          className="action-btn"
          onClick={handleLocate}
          disabled={!device || lat == null || lon == null}
          title="Pan map to this subscriber's last known location"
        >
          LOCATE ON MAP
        </button>
        <button
          type="button"
          className="action-btn"
          onClick={handleLivePing}
          disabled={!device || busyAction === "locate"}
          title="Ask the radio to report its current GPS fix"
        >
          LIVE PING
        </button>
        <button
          type="button"
          className="action-btn"
          onClick={() => setShowText((v) => !v)}
          disabled={!device}
          title="Send a one-off text message to this subscriber"
        >
          {showText ? "CLOSE TEXT" : "SEND TEXT"}
        </button>
        <button
          type="button"
          className="action-btn disabled"
          disabled
          title="Direct dispatcher \u2192 radio call needs the Phase 3 audio pipeline. The wire (PTT_REQUEST PrivateVoiceStart) is supported, but without microphone capture the call would be silent."
        >
          DIRECT CALL
        </button>
        <button
          type="button"
          className="action-btn disabled"
          disabled
          title="Call Alert in the FleetConsole protocol is radio\u2192dispatcher only. The OP_CALLALERT permission lets radios send alerts; there is no dispatcher\u2192radio CA frame."
        >
          CALL ALERT
        </button>
      </div>

      {showText ? (
        <div className="sub-flyout-text">
          <textarea
            value={textDraft}
            onChange={(e) => setTextDraft(e.target.value)}
            placeholder={`Message to ${u.Name || u.Login}\u2026`}
            rows={3}
            autoFocus
            onKeyDown={(e) => {
              // Cmd/Ctrl+Enter sends.
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                handleSendText();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setShowText(false);
              }
            }}
          />
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button type="button" className="action-btn" onClick={() => setShowText(false)}>
              CANCEL
            </button>
            <button
              type="button"
              className="action-btn primary"
              onClick={handleSendText}
              disabled={busyAction === "text" || !textDraft.trim()}
            >
              SEND
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="sub-flyout-row">
      <span className="sub-flyout-label">{label}</span>
      <span className="sub-flyout-value">{children}</span>
    </div>
  );
}

function Dim({ children }: { children: React.ReactNode }) {
  return <span style={{ color: "var(--steel-700)" }}>{children}</span>;
}

function formatAge(ms: number): string {
  if (ms < 0) return "0s";
  const s = Math.floor(ms / 1000);
  if (s < 60)   return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60)   return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}
